import { DegreeInput } from './DegreeInput';
import { PrecisionNumberInput } from './PrecisionNumberInput';
import { useFieldValue } from './useFieldValue';
import type { FieldComponentProps } from '@/data/registry';
import { useState } from 'react';
import { Euler, type EulerOrder, Quaternion } from 'three';

type QuaternionLike = { x: number; y: number; z: number; w: number };

const EULER_ORDERS: EulerOrder[] = ['XYZ', 'YZX', 'ZXY', 'ZYX', 'YXZ', 'XZY'];

export function QuaternionField({
  object,
  field,
  label,
}: FieldComponentProps<QuaternionLike>) {
  const [eulerOrder, setEulerOrder] = useState<EulerOrder>('XYZ');

  const quaternion = useFieldValue(object, field);
  const euler = new Euler(0, 0, 0, eulerOrder);
  euler.setFromQuaternion(
    new Quaternion(quaternion.x, quaternion.y, quaternion.z, quaternion.w),
  );

  const updateQuaternion = (axis: keyof QuaternionLike, value: number) => {
    const nextValue = {
      x: quaternion.x,
      y: quaternion.y,
      z: quaternion.z,
      w: quaternion.w,
      [axis]: value,
    };
    const length = Math.sqrt(
      nextValue.x ** 2 + nextValue.y ** 2 + nextValue.z ** 2 + nextValue.w ** 2,
    );
    if (length === 0) return; // Prevent division by zero

    const normalized = {
      x: nextValue.x / length,
      y: nextValue.y / length,
      z: nextValue.z / length,
      w: nextValue.w / length,
    };

    object.set(field, normalized);
  };

  const updateEuler = (axis: keyof typeof euler, value: number) => {
    const nextEuler = {
      x: euler.x,
      y: euler.y,
      z: euler.z,
      [axis]: value,
    };

    const newEuler = new Euler(
      nextEuler.x,
      nextEuler.y,
      nextEuler.z,
      eulerOrder,
    );
    const quaternion = new Quaternion();
    quaternion.setFromEuler(newEuler);

    object.set(field, quaternion);
  };

  const handleEulerOrderChange = (newOrder: EulerOrder) => {
    setEulerOrder(newOrder);
    object.set(field, quaternion); // Trigger update to recalculate Euler angles with the new order
  };

  return (
    <div className="mt-1.5 text-[0.78rem] leading-tight">
      <details className="group">
        <summary className="flex cursor-pointer flex-row items-center text-[0.68rem] font-medium tracking-[0.04em] text-white/65 select-none marker:content-none hover:text-white/80">
          {label}
          <div className="ml-1 text-xs text-white/50 transition-transform group-open:-rotate-90">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="inline-block h-3 w-3"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </summary>
        <div className="mt-2 space-y-2 rounded border border-white/12 bg-[#0d1928e6] p-2">
          {/* Euler Angles Section */}
          <div>
            <label className="mb-1.5 block text-[0.68rem] font-medium tracking-[0.04em] text-white/65">
              Euler Angles
            </label>
            <div className="mb-2 flex items-center gap-2">
              <label className="text-[0.68rem] text-white/50">Order:</label>
              <select
                value={eulerOrder}
                onChange={e =>
                  handleEulerOrderChange(e.target.value as EulerOrder)
                }
                className="rounded border border-white/12 bg-[#0d1928e6] px-2 text-[0.72rem] text-white transition-colors outline-none focus:border-white/30"
              >
                {EULER_ORDERS.map(order => (
                  <option key={order} value={order}>
                    {order}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <DegreeInput
                value={euler.x}
                onChange={value => updateEuler('x', value)}
              />
              <DegreeInput
                value={euler.y}
                onChange={value => updateEuler('y', value)}
              />
              <DegreeInput
                value={euler.z}
                onChange={value => updateEuler('z', value)}
              />
            </div>
          </div>

          {/* Quaternion Section */}
          <div>
            <label className="mb-1.5 block text-[0.68rem] font-medium tracking-[0.04em] text-white/65">
              Quaternion
            </label>
            <div className="grid grid-cols-4 gap-1.5">
              <div>
                <label className="text-[0.65rem] text-white/50">X</label>
                <PrecisionNumberInput
                  value={quaternion.x}
                  onChange={value => updateQuaternion('x', value)}
                />
              </div>
              <div>
                <label className="text-[0.65rem] text-white/50">Y</label>
                <PrecisionNumberInput
                  value={quaternion.y}
                  onChange={value => updateQuaternion('y', value)}
                />
              </div>
              <div>
                <label className="text-[0.65rem] text-white/50">Z</label>
                <PrecisionNumberInput
                  value={quaternion.z}
                  onChange={value => updateQuaternion('z', value)}
                />
              </div>
              <div>
                <label className="text-[0.65rem] text-white/50">W</label>
                <PrecisionNumberInput
                  value={quaternion.w}
                  onChange={value => updateQuaternion('w', value)}
                />
              </div>
            </div>
          </div>
        </div>
      </details>
    </div>
  );
}
